import AudioToggleIcon from "./lib/audio_toggle_icon";
import GlobalAudioController from "./lib/global_audio_controller";
import LiftoffMRAID from "./madlib/liftoff_mraid";
import {
  AUDIO_POLICY_ALWAYS_MUTE,
  AUDIO_POLICY_ON_INTERACTION,
  EXCHANGE_DOUBLECLICK,
} from "./constants";
import * as Utils from "./frontendlib/utils";

const CLOSE_Z = 99;
const WATERMARKS_Z = 98;

/**
 * Returns a close element with a countdown timer that starts at `delay` seconds.
 */
const createCloseEl = (delay) => {
  const closeEl = document.createElement("div");
  closeEl.id = "liftoff-close";
  Object.assign(closeEl.style, {
    display: "block",
    position: "absolute",
    top: "10px",
    right: "10px",
    width: "32px",
    height: "32px",
    zIndex: CLOSE_Z,
  });
  const buttonStyles = {
    width: "100%",
    height: "100%",
    display: "block",
    position: "absolute",
    top: "0",
    left: "0",
    fontSize: "16px",
    lineHeight: "28px",
    textAlign: "center",
    border: "2px solid #FFF",
    borderRadius: "50%",
    boxSizing: "border-box",
    pointerEvents: "all",
  };
  const closeTargetEl = document.createElement("div");
  closeTargetEl.classList.add("liftoff-close-target");
  Object.assign(closeTargetEl.style, buttonStyles, {
    color: "#EEE",
    backgroundColor: "#000",
  });
  // This is a nice close X.
  closeTargetEl.innerHTML = "&#10005";
  closeTargetEl.addEventListener("click", (e) => {
    LiftoffMRAID.close();
    e.stopPropagation();
  });
  closeEl.appendChild(closeTargetEl);
  if (delay > 0) {
    const closeDelayEl = document.createElement("div");
    closeDelayEl.classList.add("liftoff-close-delay");
    Object.assign(closeDelayEl.style, buttonStyles, {
      color: "#555",
      backgroundColor: "#BBB",
    });
    let countdown = delay;
    closeDelayEl.textContent = countdown;
    closeDelayEl.style.display = "block";
    const closeDelayInterval = setInterval(() => {
      countdown -= 1;
      if (countdown > 0) {
        closeDelayEl.textContent = countdown;
        return;
      }
      closeDelayEl.style.display = "none";
      clearInterval(closeDelayInterval);
    }, 1000);
    closeEl.appendChild(closeDelayEl);
  }
  return closeEl;
};

/**
 * Calls `callback` on the user's first interaction.
 */
const onFirstInteraction = (callback) => {
  let hasUserInteracted = false;
  const handleFirstInteraction = (e) => {
    window.removeEventListener("mousedown", handleFirstInteraction, true);
    window.removeEventListener("touchstart", handleFirstInteraction, true);
    if (hasUserInteracted) return;
    hasUserInteracted = true;
    callback(e);
  };
  window.addEventListener("mousedown", handleFirstInteraction, true);
  window.addEventListener("touchstart", handleFirstInteraction, true);
};

/**
 * Creates an icon in the lower-right corner that allows the user to globally toggle the creative's audio on
 * and off. The toggle only appears on creatives that have autoplay audio (i.e. audio that plays prior to user
 * interaction). For creatives that have autoplay audio, the creative is globally muted by default but will
 * become globally unmuted when the user interacts with the creative.
 */
const createGlobalAudioToggle = () => {
  const el = document.createElement("div");
  el.classList.add("liftoff-global-audio-toggle");
  Object.assign(el.style, {
    opacity: "0.5",
    display: "none",
    pointerEvents: "all",
    right: "0",
    bottom: "0",
    position: "absolute",
  });
  const icon = new AudioToggleIcon();
  const iconEl = icon.getElement();
  el.appendChild(iconEl);

  let isCurrentlyMuted = false;
  const setMuted = (shouldMute) => {
    if (shouldMute === isCurrentlyMuted) return;
    isCurrentlyMuted = shouldMute;
    GlobalAudioController.setMuted(shouldMute);
    icon.setMuted(shouldMute);
  };

  let hasUserInteracted = false;
  onFirstInteraction((e) => {
    hasUserInteracted = true;
    // Interactions with the global audio toggle icon are handled by the click listener below.
    if (el.contains(e.target)) return;
    setMuted(false);
  });

  iconEl.addEventListener("click", () => {
    setMuted(!isCurrentlyMuted);
  });

  GlobalAudioController.runAfterFirstSound(() => {
    if (hasUserInteracted) return;
    el.style.display = "inline-block";
  });

  setMuted(true);
  return el;
};

const globallyMuteAllAudio = () => {
  GlobalAudioController.setMuted(true);
};

/**
 * Performs various operations required to conform to exchange- and/or country-specific creative policies.
 * This is used by both madlib HTML and CSP (third-party) creatives.
 * Requires:
 *   - creativePolicy - An object with keys:
 *       addBorder (boolean)
 *       showAdChoices (boolean)
 *       showChineseWatermark (boolean)
 *       russianAgeRating (number)
 *   - bidContext - An object with keys: { creativeID, platform, exchange, sourceAppName, sourceAppID }
 */
export const applyLiftoffPolicy = (
  snippetEl,
  {
    addBorder,
    showAdChoices,
    russianAgeRating,
    showChineseWatermark,
    useCustomClose,
    closeDelay,
    audioPolicy,
  },
  { creativeID, platform, exchange, sourceAppName, sourceAppID, cdnURL }
) => {
  const watermarksEl = document.createElement("div");
  watermarksEl.classList.add("liftoff-watermarks");
  Object.assign(watermarksEl.style, {
    position: "absolute",
    bottom: "0",
    left: "0",
    zIndex: `${WATERMARKS_Z}`,
    height: "18px",
    pointerEvents: "none",
  });

  /**
   * Creates an element to apply a border around the whole creative (required for banners on certain
   * exchanges). We do this rather than adding a border directly to `#liftoff-snippet`, because the latter
   * changes the size of the snippet content by 2 * border-width, resulting in slightly inconsistent
   * rendering depending on whether the border is there or not.
   */
  if (addBorder) {
    const el = document.createElement("div");
    el.id = "liftoff-border";
    Object.assign(el.style, {
      width: "100%",
      height: "100%",
      position: "absolute",
      border: "1px solid #AAA",
      boxSizing: "border-box",
      zIndex: "10",
      pointerEvents: "none",
    });
    document.body.appendChild(el);
  }

  if (showAdChoices) {
    // NOTE(Austin): We append these parameters for Google Analytics and more
    // granular cloudfront logs.
    const queryParams = {
      creative_id: creativeID,
      platform: platform,
      exchange: exchange,
      source_app_name: sourceAppName,
      source_app_id: sourceAppID,
    };

    const a = document.createElement("a");
    a.classList.add("ad-choices-icon");
    const optoutPageURL = Utils.appendParamObjectToURL(
      `${cdnURL}/optout.html`,
      queryParams
    );

    if (exchange === EXCHANGE_DOUBLECLICK) {
      // NOTE (Austin): Letting the click from the anchor tag intentionally take
      // the user out of the ad provides a better experience, as the in-app
      // browser is broken on some inventory (e.g., DoubleClick). Some
      // exchanges, such as Unity, open the in-app browser regardless, leading to
      // a poor user experience. Additionally, certain exchanges ignore our
      // mraid.open or window.open parameters, opening the clickthrough URL in a
      // new browser anyway.
      // TODO(Austin): Test making this the default.
      a.setAttribute("target", "_blank");
      a.setAttribute("href", optoutPageURL);
    } else {
      // NOTE(oscar): Chartboost recommends we open the ad choices link using
      // MRAID.
      a.addEventListener("click", (e) => {
        e.preventDefault();
        LiftoffMRAID.open(optoutPageURL, { exchange, platform });
      });
    }

    Object.assign(a.style, {
      width: "20px",
      height: "100%",
      display: "inline-block",
      backgroundColor: "rgba(204, 204, 204, 0.6)",
      backgroundImage: `url(${cdnURL}/adchoices.png)`,
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "12px 12px",
      pointerEvents: "all",
    });
    watermarksEl.appendChild(a);
  }

  if (russianAgeRating != null) {
    const ageTag = document.createElement("div");
    ageTag.textContent = `${russianAgeRating}+`;
    ageTag.classList.add("age-rating");
    Object.assign(ageTag.style, {
      color: "blue",
      fontSize: "10px",
      fontWeight: "500",
      background: "whitesmoke",
      borderRadius: "0 5px 0 0",
      padding: "0 3px",
      opacity: "0.75",
      display: "inline",
      verticalAlign: "bottom",
      pointerEvents: "none",
      lineHeight: "20px",
    });
    watermarksEl.appendChild(ageTag);
  }

  if (showChineseWatermark) {
    const el = document.createElement("div");
    el.classList.add("chinese-watermark");
    Object.assign(el.style, {
      display: "inline-block",
      height: "100%",
      width: "42px",
      pointerEvents: "none",
      backgroundColor: "rgba(204, 204, 204, 0.6)",
      backgroundImage: `url(${cdnURL}/chinese_watermark.png)`,
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
    });
    watermarksEl.appendChild(el);
  }

  if (audioPolicy === AUDIO_POLICY_ALWAYS_MUTE) {
    globallyMuteAllAudio();
  } else if (audioPolicy === AUDIO_POLICY_ON_INTERACTION) {
    const globalAudioToggleEl = createGlobalAudioToggle();
    snippetEl.appendChild(globalAudioToggleEl);
  }

  LiftoffMRAID.useCustomClose(useCustomClose).then((isUsingCustomClose) => {
    if (!isUsingCustomClose) return;
    const closeEl = createCloseEl(closeDelay);
    snippetEl.appendChild(closeEl);
  });

  snippetEl.appendChild(watermarksEl);
  return snippetEl;
};

export const postHTMLReturn = (adexpEndpoint, html) => {
  return Utils.request(
    "POST",
    `${adexpEndpoint}/html_return`,
    {
      // Don't set the content type to "application/json" because that requires
      // a preflight OPTIONS request (because this is a cross-domain request).
      "Content-Type": "text/plain",
    },
    html
  );
};

export const getDOMSnapshot = () => document.documentElement.outerHTML;

/**
 * Given `adexpEndpoint` (the AdExp host as a `String`), this sends an HTML
 * return after `htmlReturnDelay` ms. Returns a `Promise` that resolves when
 * the HTML return HTTP request is complete.
 */
export const configureHTMLReturn = (adexpEndpoint, htmlReturnDelay) => {
  const postHTML = () => {
    console.info("Returning HTML");
    return postHTMLReturn(adexpEndpoint, getDOMSnapshot());
  };

  if (htmlReturnDelay == null || htmlReturnDelay === 0) {
    // NOTE(oscar): A delay of 0 ideally should return the DOM before any JS
    // manipulation, so we capture the state of the DOM immediately.
    return postHTML();
  }

  return new Promise((resolve, reject) => {
    setTimeout(() => postHTML().then(resolve, reject), htmlReturnDelay);
  });
};



// WEBPACK FOOTER //
// ./src/js/creative_utils.js